import { supabase } from '../api/supabase'

// The single source every PDF/letterhead reads company details from -- see
// 20260928b_company_settings_invoice_fields.sql (a strict superset of the never-applied
// 20260916b_company_settings_address_update.sql -- that original migration is superseded, don't
// run it separately). Templates must not hardcode CY UK's address/phone/registration/bank/VAT
// text; if any of it changes again, this table is the only edit.
export interface CompanySettings {
  legal_name: string
  address_line_1: string | null
  address_line_2: string | null
  address_line_3: string | null
  postcode: string | null
  country: string | null
  phone: string | null
  company_no: string | null
  bifa_line: string | null
  logo_url: string | null
  bifa_logo_url: string | null
  vat_number: string | null
  bank_name: string | null
  bank_account_number: string | null
  bank_sort_code: string | null
  bank_iban: string | null
  bank_swift: string | null
}

// Mirrors the row the migration seeds -- used only if the fetch itself fails (e.g. a
// network blip), so an outage can't silently ship the old deprecated address on a live
// document instead of just failing loudly. Wording verified against the real sample PDFs
// (docs/warehouse/samples/*_SAMPLE.pdf, docs/invoice-samples/*.pdf), not a guessed placeholder.
const FALLBACK: CompanySettings = {
  legal_name: 'CY UK Limited',
  address_line_1: 'Unit 38, Minworth Industrial Estate',
  address_line_2: 'Forge Lane, Sutton Coldfield',
  address_line_3: null,
  postcode: 'B76 1AH',
  country: 'United Kingdom',
  phone: '+44(0)121 4774648',
  company_no: '06056764',
  bifa_line: 'CY UK Limited is the trading member of the British International Freight Association (BIFA). All business is undertaken subject to the Standard Trading Conditions of the BIFA (Latest Edition). Copies available upon request.',
  logo_url: null,
  bifa_logo_url: null,
  vat_number: 'GB919764479',
  bank_name: 'BARCLAYS BANK 161 HIGH STREET KINGS HEATH BIRMINGHAM WEST MIDLANDS B14 7LA UNITED KINGDOM',
  bank_account_number: '00599395',
  bank_sort_code: '200844',
  bank_iban: 'GB61 BUKB 2008 4400 5993 95',
  bank_swift: 'BUKBGB22',
}

let cached: CompanySettings | null = null

export async function getCompanySettings(): Promise<CompanySettings> {
  if (cached) return cached
  const { data } = await supabase.from('company_settings').select('*').eq('id', 'default').maybeSingle()
  cached = (data as CompanySettings | null) || FALLBACK
  return cached
}

// Admin > Company Settings calls this after saving so the next PDF picks up the edit
// without a full page reload.
export function invalidateCompanySettingsCache() {
  cached = null
}

export function companyAddressLines(s: CompanySettings): string[] {
  return [s.address_line_1, s.address_line_2, s.address_line_3, s.postcode, s.country].filter((l): l is string => !!l)
}

// "CY UK Limited. Registered in England No. 06056764. Registered Office: Unit 38, Minworth
// Industrial Estate, Forge Lane, Sutton Coldfield B76 1AH." -- exact structure verified
// against the real sample PDFs' footer, with the registered-office clause built from the
// address fields (not baked into static text) so it stays correct if the address changes again.
export function companyRegistrationLine(s: CompanySettings): string {
  const officeAddr = [s.address_line_1, s.address_line_2, s.postcode].filter(Boolean).join(', ')
  const base = `${s.legal_name}. Registered in England No. ${s.company_no || '—'}.`
  return officeAddr ? `${base} Registered Office: ${officeAddr}.` : base
}
